import React, { useState } from 'react';
import { truncateText } from './utils';
import { colors, typography, layout } from '../styles/minimalStyles';

const ArticleCard = ({ article, onClick, layout = 'grid' }) => {
  const [isHovered, setIsHovered] = useState(false);

  if (!article) return null;

  const excerptLength = layout === 'featured-large' ? 180 : 100;

  // Base styles
  const baseStyles = {
    card: {
      backgroundColor: colors.white,
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      overflow: 'hidden',
      transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      height: '100%', // Fill grid space
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)', // Subtle base shadow
      transform: 'translateY(0)',
    },
    imageContainer: {
      width: '100%',
      height: layout === 'featured-large' ? '300px' : '200px', // Adjust height based on layout
      overflow: 'hidden',
      backgroundColor: colors.backgroundLight, // Placeholder bg
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block',
    },
    content: {
      padding: '15px 20px 20px',
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    category: {
      fontSize: '0.75rem',
      fontWeight: typography.boldWeight,
      color: colors.secondaryText,
      textTransform: 'uppercase',
      marginBottom: '8px',
      display: 'inline-block',
    },
    title: {
      fontSize: layout === 'featured-large' ? '1.5rem' : typography.smallTitleSize,
      fontWeight: typography.boldWeight,
      margin: '0 0 10px 0',
      lineHeight: 1.3,
      color: colors.primaryText,
    },
    excerpt: {
      fontSize: '0.9rem',
      lineHeight: 1.5,
      color: '#555',
      marginBottom: '15px',
      flexGrow: 1, // Push meta down
    },
    meta: {
      fontSize: '0.8rem',
      color: colors.secondaryText,
      marginBottom: '10px',
    },
    readMore: {
      fontSize: '0.9rem',
      fontWeight: typography.boldWeight,
      color: colors.accent,
      textDecoration: 'none',
      transition: 'color 0.2s ease',
    },
  };

  // Hover styles
  const hoverStyles = {
    card: {
      boxShadow: '0 6px 12px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-3px)',
    },
    readMore: {
      color: colors.accentDark,
    },
  };

  // Combine styles based on hover state
  const cardStyle = isHovered
    ? { ...baseStyles.card, ...hoverStyles.card }
    : baseStyles.card;

  const readMoreStyle = isHovered
    ? { ...baseStyles.readMore, ...hoverStyles.readMore }
    : baseStyles.readMore;

  return (
    <div
      style={cardStyle}
      onClick={() => onClick(article.articlepagehref)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="link"
      tabIndex={0} // Make it focusable
      onKeyPress={(e) => (e.key === 'Enter' || e.key === ' ') && onClick(article.articlepagehref)}
    >
      {article.imageSrc && (
        <div style={baseStyles.imageContainer}>
          <img
            src={article.imageSrc}
            alt={article.imageSrcAlt || `Image for ${article.title}`}
            style={baseStyles.image}
            loading="lazy"
          />
        </div>
      )}
      <div style={baseStyles.content}>
        {article.category && (
          <span style={baseStyles.category}>{article.category}</span>
        )}
        <h3 style={baseStyles.title}>{article.title}</h3>
        <p style={baseStyles.excerpt}>
          {truncateText(article.excerpt || article.content, excerptLength)}
        </p>
        <div style={baseStyles.meta}>
          <span>{article.date}</span>
          {article.author && <span> &bull; {article.author}</span>}
        </div>
        <span style={readMoreStyle}>Read More &rarr;</span>
      </div>
    </div>
  );
};

export default ArticleCard;